import { useTranslation } from "react-i18next";
import { cn, DraggableWindow } from "@tellescope/ui";
import { useCallWindow } from "./useCallWindow";
import { IncomingCallView } from "./views/IncomingCallView";
import { ActiveCallView } from "./views/ActiveCallView";
import { AddAttendeesPanel } from "./views/AddAttendeesPanel";

export interface CallWindowProps {
  /** Additional className for the window */
  className?: string;
}

/**
 * CallWindow - Main draggable window component for phone calls.
 *
 * Displays incoming calls, active call UI, and add attendees panel.
 * Uses the DraggableWindow component for positioning and drag behavior.
 *
 * This component should be rendered at the app root level (like toasts)
 * and will automatically show/hide based on call state.
 *
 * @example
 * ```tsx
 * // In App.tsx or layout
 * function App() {
 *   return (
 *     <>
 *       <MainContent />
 *       <CallWindow />
 *     </>
 *   );
 * }
 * ```
 */
export function CallWindow({ className }: CallWindowProps) {
  const { t } = useTranslation("calling");
  const {
    isVisible,
    activeCall,
    callStatus,
    callDirection,
    isMuted,
    activeCallMeta,
    incomingCalls,
    hasIncomingCalls,
    conference,
    isInConference,
    participants,
    showAddAttendees,
    setShowAddAttendees,
    handleAnswer,
    handleReject,
    handleHangUp,
    handleToggleMute,
    handleSendDigits,
    handleAddAttendeeByUser,
    handleAddAttendeeByPhone,
  } = useCallWindow();

  // Translate status label based on call status using literal keys for extraction
  const getStatusLabel = () => {
    switch (callStatus) {
      case "connecting":
        return t("Connecting...");
      case "ringing":
        return t("Ringing...");
      case "open":
        return t("Connected");
      case "disconnected":
        return t("Call ended");
      default:
        return t("Connecting...");
    }
  };

  // Don't render if not visible
  if (!isVisible) {
    return null;
  }

  return (
    <DraggableWindow
      open={isVisible}
      title={t("Phone Call")}
      persistKey="call-window"
      minimizable
      width={360}
      initialPosition="bottom-right"
      constrainToViewport
      viewportPadding={20}
      showCloseButton={false}
      className={className}
    >
      <div className={cn("flex flex-col", activeCall && "min-h-[300px]")}>
        {/* Incoming calls section - shown at top when there are incoming calls */}
        {hasIncomingCalls && (
          <IncomingCallView incomingCalls={incomingCalls} onAnswer={handleAnswer} onReject={handleReject} />
        )}

        {/* Active call section - shown when there's an active call */}
        {activeCall && (
          <>
            {/* Main call view */}
            {!showAddAttendees ? (
              <ActiveCallView
                direction={callDirection}
                status={callStatus}
                statusLabel={getStatusLabel()}
                startTime={activeCallMeta.startTime}
                activeCallMeta={activeCallMeta}
                isMuted={isMuted}
                isConnected={callStatus === "open"}
                isConference={isInConference}
                conference={conference}
                participants={participants}
                onToggleMute={handleToggleMute}
                onHangUp={handleHangUp}
                onOpenAddAttendees={() => setShowAddAttendees(true)}
                onSendDigits={handleSendDigits}
              />
            ) : (
              // Add attendees panel (replaces main view when open)
              <div className="flex-1 flex flex-col">
                <AddAttendeesPanel
                  onClose={() => setShowAddAttendees(false)}
                  onAddUser={handleAddAttendeeByUser}
                  onAddPhone={handleAddAttendeeByPhone}
                  existingAttendees={conference?.users ?? []}
                  className="flex-1"
                />
              </div>
            )}
          </>
        )}
      </div>
    </DraggableWindow>
  );
}
